import { useState, useMemo, useRef, useEffect, useCallback, memo } from 'react';
import { useSessionStorageState } from '@reflex/ui/hooks/useWebStorageState';
import { AnimatePresence, motion } from 'motion/react';
import { staggerContainer } from '@/lib/motion';
import { cn } from '@reflex/ui/lib/utils';
import {
  getNonArchivedAgents,
  getArchivedAgents,
  getPinnedAgents,
  getUnpinnedAgents,
  mergeAgentsById,
  groupSidebarSessionsByTime,
  groupSidebarSessionsByGroup,
  agentSortTimestamp,
  filterDrafts,
  type AgentTimeBucket,
  type SidebarSectionContext,
} from '@/lib/agent-utils';
import { useSessionSort } from './useSessionSort';
import { SESSION_VIEW_GROUPS, useSessionView } from './useSessionView';
import { AgentSidebarViewMenu } from './AgentSidebarViewMenu';
import { getTimeBucketBounds } from '@reflex/ui/lib/time-buckets';
import { SidebarHeader } from '@reflex/ui/components/sidebar/SidebarHeader';
import { ScrollArea } from '@reflex/ui/components/ui/scroll-area';
import { Plus } from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { Button } from '@reflex/ui/components/ui/button';
import { CollapseSidebarButton } from '@reflex/ui/components/sidebar/CollapseSidebarButton';
import { ToolbarIconButton } from '@reflex/ui/components/shared/ToolbarIconButton';
import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import {
  useAgents,
  usePinnedAgents,
  useArchiveAgent,
  useArchiveAllAgents,
  useUnarchiveAgent,
  usePinAgent,
  useUnpinAgent,
  useBulkArchiveAgents,
  useBulkInterruptAgents,
  useBulkKillAgents,
  useAgentSearch,
} from '@/hooks/agents/useAgents';
import { useAuth } from '@/hooks/auth/useAuth';
import { useDebouncedValue } from '@reflex/ui/hooks/useDebouncedValue';
import { draftFiltersFrom, useAgentFilters } from '@/hooks/agents/useAgentFilters';
import { useAgentListCompletion } from '@/hooks/agents/useAgentListCompletion';
import { AgentFilterBar } from './AgentFilterBar';
import { searchFieldShellClasses } from '@/components/shared/search-field-shell';
import { useLiveAgentTurnSignals } from '@/hooks/agents/useLiveAgentTurnSignals';
import {
  activityFromDurableAgentState,
  hasBackgroundOnlyWork,
} from '@/hooks/agents/agent-activity-utils';
import { useAgentViews } from '@/hooks/agents/useAgentViews';
import { shouldConfirmArchive } from '@/lib/status-colors';
import { DeleteAgentDialog } from './agent-actions/DeleteAgentDialog';
import { useStopViewingDeletedAgents } from '@/hooks/agents/useDeleteAgent';
import type { Agent, AgentActivity } from '@/lib/types';
import { AgentSidebarSessionGroup } from './agent-sidebar/AgentSidebarSessionGroup';
import { useAgentDrafts, useDeleteAgentDraft } from '@/hooks/agents/useAgentDrafts';
import type { AgentDraft } from '@/lib/types';
import { ArchivedAgentSection } from './agent-sidebar/ArchivedAgentSection';
import { PinnedAgentSection } from './agent-sidebar/PinnedAgentSection';
import { DraftsSection } from './agent-sidebar/DraftsSection';
import { AgentSelectionBar } from './AgentSelectionBar';
import { AddToGroupDialog } from './AddToGroupDialog';
import { AddLabelDialog } from './AddLabelDialog';

/** How many server-side search hits to graft onto the loaded pages. */
const SEARCH_RESULT_LIMIT = 50;
/** A single character matches nearly every run; wait for a real query. */
const MIN_SERVER_SEARCH_LENGTH = 2;
/** Quiet period before a typed query becomes a request. */
const SEARCH_DEBOUNCE_MS = 250;

interface AgentSidebarProps {
  selectedId: string | null;
  selectedDraftId?: string | null;
  onSelect: (agent: Agent) => void;
  onSelectDraft?: (draft: AgentDraft) => void;
  /**
   * Stop viewing the open run. `next` is the run that should take its place —
   * the sidebar knows which one that is, and handing it over lets the page
   * navigate straight there instead of bouncing through the runs index and
   * rendering the dashboard for a frame on the way.
   */
  onDeselect: (next?: Agent) => void;
  onToggle: () => void;
  onLaunchAgent: () => void;
  /**
   * Start a run already filed into a group, from the "+" on that group's
   * heading in the group view. Optional so surfaces that render the sidebar
   * without a launcher (previews, stories) simply show no "+".
   */
  onLaunchInGroup?: (group: SidebarSectionContext) => void;
  mobile?: boolean;
  /**
   * `dock`: the sidebar is the app shell's left bar (`SessionDock`). The
   * panel header disappears — launch and collapse live in the dock chrome —
   * and the search + filter cluster renders as one combined bar.
   */
  variant?: 'panel' | 'dock';
}

export const AgentSidebar = memo(function AgentSidebar({
  selectedId,
  selectedDraftId = null,
  onSelect,
  onSelectDraft,
  onDeselect,
  onToggle,
  onLaunchAgent,
  onLaunchInGroup,
  mobile,
  variant = 'panel',
}: AgentSidebarProps) {
  const { user: currentUser } = useAuth();
  // Read state for the `Unread` facet. Both are shared store/React Query inputs,
  // so subscribing here is cheap — the rows dedupe the same `useAgentViews`
  // request, and the turn-signal store fans out from one module-global
  // snapshot. They have to land before `useAgentFilters` so the hook can fold
  // them into `filterAgents`.
  const unreadViews = useAgentViews();
  const turnActivities = useLiveAgentTurnSignals();
  const agentFilters = useAgentFilters(currentUser?.id ?? null, {
    unreadViews,
    unreadActivities: turnActivities,
  });
  const {
    data: loadedAgents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    pagesLoaded,
  } = useAgents(agentFilters.serverFilters);
  // Pinned runs are fetched separately so a pin that sits past the loaded
  // infinite-scroll pages still shows up. Graft any the paginated list has not
  // reached onto it before the rest of the sidebar filters/groups the set.
  const { data: pinnedAgents } = usePinnedAgents(agentFilters.filters.userId);
  // Searching only the loaded pages made the box look broken: a run you knew
  // existed simply was not there until you had scrolled far enough to load it.
  // Ask the server too and graft the hits on; local filtering then applies to
  // them like any other row.
  const searchQuery = agentFilters.filters.query?.trim() ?? '';
  // Local filtering runs on every keystroke; the request behind it waits for a
  // pause, so a typed-out word is one query rather than one per character.
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const { data: searchedAgents } = useAgentSearch(debouncedQuery, SEARCH_RESULT_LIMIT, {
    enabled: debouncedQuery.length >= MIN_SERVER_SEARCH_LENGTH,
    userId: agentFilters.filters.userId,
  });
  const allAgents = useMemo(
    () => mergeAgentsById(loadedAgents ?? [], pinnedAgents ?? [], searchedAgents),
    [loadedAgents, pinnedAgents, searchedAgents],
  );
  const { data: allDrafts = [] } = useAgentDrafts();
  const deleteDraft = useDeleteAgentDraft();
  const archiveAgent = useArchiveAgent();
  const archiveAllAgents = useArchiveAllAgents();
  const unarchiveAgent = useUnarchiveAgent();
  const pinAgent = usePinAgent();
  const unpinAgent = useUnpinAgent();
  const bulkArchive = useBulkArchiveAgents();
  const bulkInterrupt = useBulkInterruptAgents();
  const bulkKill = useBulkKillAgents();
  const [showArchived, setShowArchived] = useSessionStorageState(
    'agent-sidebar-show-archived',
    false,
  );
  const [pinnedCollapsed, setPinnedCollapsed] = useSessionStorageState(
    'agent-sidebar-pinned-collapsed',
    false,
  );
  const [draftsCollapsed, setDraftsCollapsed] = useSessionStorageState(
    'agent-sidebar-drafts-collapsed',
    false,
  );
  // Keyed by section id: a time bucket in the time views, a group id in the
  // group view. One set for both, so collapsing a section in one view has no
  // say over the other's sections.
  const [collapsedGroupsArr, setCollapsedGroupsArr] = useSessionStorageState<string[]>(
    'agent-sidebar-collapsed-groups',
    [],
  );
  const collapsedGroups = useMemo(() => new Set(collapsedGroupsArr), [collapsedGroupsArr]);
  const toggleGroup = useCallback(
    (key: string) => {
      setCollapsedGroupsArr((prev) => {
        const set = new Set(prev);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        return [...set];
      });
    },
    [setCollapsedGroupsArr],
  );

  const { filterAgents, filters } = agentFilters;
  const filtersActive = useMemo(() => {
    return (
      filters.userId !== null ||
      filters.status !== null ||
      filters.repoSlug !== null ||
      filters.timeRange !== null ||
      filters.draftsOnly ||
      filters.unreadOnly ||
      filters.archivedOnly ||
      Boolean(filters.query?.trim())
    );
  }, [filters]);
  const agents = useMemo(() => filterAgents(allAgents ?? []), [filterAgents, allAgents]);
  const filteredDrafts = useMemo(
    () => filterDrafts(allDrafts, draftFiltersFrom(filters)),
    [allDrafts, filters],
  );

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    // Observe against the list's own viewport: with no root the margin
    // extends the *window*, and the sentinel only ever counts as visible once
    // it is scrolled fully into the sidebar. That left the list stuck after a
    // page that added nothing visible — the sentinel a few pixels past the
    // edge, close enough by the margin but never asked again.
    const root = el.closest<HTMLElement>('[data-slot="scroll-area-viewport"]');
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root, rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const [sessionSort] = useSessionSort();
  const [sessionView] = useSessionView();
  // The group view replaces the time buckets with sections, and drops the
  // bucket-scoped "archive all" along with them.
  const groupedByGroupView = sessionView === SESSION_VIEW_GROUPS;
  // Pull pages until what is on screen reflects the org rather than the pages
  // that happen to be loaded. The group view asks for the whole list: it
  // derives its sections from the runs it can see, so a group whose members
  // are all older than page one has no section at all — the list looks like
  // it lost a group. A client-only filter only needs a screenful.
  useAgentListCompletion({
    enabled: groupedByGroupView || agentFilters.clientFilterActive,
    matchedCount: agents.length,
    targetRows: groupedByGroupView ? Infinity : undefined,
    pagesLoaded,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const activeAgents = useMemo(() => getNonArchivedAgents(agents), [agents]);
  const archivedAgents = useMemo(
    () =>
      getArchivedAgents(agents).sort(
        (a, b) => agentSortTimestamp(b, sessionSort) - agentSortTimestamp(a, sessionSort),
      ),
    [agents, sessionSort],
  );
  const sortedActive = useMemo(
    () =>
      [...activeAgents].sort(
        (a, b) => agentSortTimestamp(b, sessionSort) - agentSortTimestamp(a, sessionSort),
      ),
    [activeAgents, sessionSort],
  );
  const pinnedActive = useMemo(() => getPinnedAgents(sortedActive), [sortedActive]);
  const unpinnedActive = useMemo(() => getUnpinnedAgents(sortedActive), [sortedActive]);
  const activities = useMemo(
    () =>
      new Map<string, AgentActivity>(
        sortedActive.map((a) => [a.id, activityFromDurableAgentState(a)]),
      ),
    [sortedActive],
  );
  // Drafts get their own pinned-at-top section (see `DraftsSection`) instead
  // of being interleaved into the time buckets, so pass none here.
  const groupedSessions = useMemo(
    () => groupSidebarSessionsByTime(unpinnedActive, [], sessionSort),
    [unpinnedActive, sessionSort],
  );
  const groupedDraftsOnly = useMemo(
    () => groupSidebarSessionsByTime([], filteredDrafts, sessionSort),
    [filteredDrafts, sessionSort],
  );
  // The group view buckets by membership instead of time. It runs over the
  // same unpinned set, so pinning and archiving behave identically in it.
  // Drafts are excluded here for the same reason as the time buckets: they
  // have their own pinned-at-top section.
  const groupedByGroup = useMemo(
    () => groupSidebarSessionsByGroup(unpinnedActive, [], sessionSort),
    [unpinnedActive, sessionSort],
  );
  const sortedDrafts = useMemo(
    () => [...filteredDrafts].sort((a, b) => b.updatedAt - a.updatedAt),
    [filteredDrafts],
  );

  const selectedIdRef = useRef(selectedId);
  // Read through a ref rather than a dependency: the activity map changes on
  // every turn boundary, and the archive handlers are passed down to memoized
  // rows that would otherwise re-render on each flip.
  const activitiesRef = useRef(activities);
  // Assigned alongside `orderedSelectableIds`, which is derived from it.
  const orderedActiveRef = useRef<Agent[]>([]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
    activitiesRef.current = activities;
  });

  // Which run replaces the open one when `archivedIds` takes it away: the
  // nearest row below it that is not itself being archived, or the nearest
  // above when it was last. Anchored on the open row rather than on the first
  // archived id — a bulk archive of a selection spread through the list would
  // otherwise measure from whichever selected row happens to sit highest and
  // hand back the top of the list. Read from a ref for the same reason as
  // `activities`: the visual order changes constantly and must not re-create
  // the archive handlers the memoized rows hold.
  const successorTo = useCallback((archivedIds: ReadonlySet<string>) => {
    const order = orderedActiveRef.current;
    const from = order.findIndex((a) => a.id === selectedIdRef.current);
    if (from === -1) return undefined;
    for (let i = from + 1; i < order.length; i++) {
      if (!archivedIds.has(order[i].id)) return order[i];
    }
    for (let i = from - 1; i >= 0; i--) {
      if (!archivedIds.has(order[i].id)) return order[i];
    }
    return undefined;
  }, []);

  const archiveMutate = archiveAgent.mutate;
  const unarchiveMutate = unarchiveAgent.mutate;
  const pinMutate = pinAgent.mutate;
  const unpinMutate = unpinAgent.mutate;

  const [confirmArchiveAgent, setConfirmArchiveAgent] = useState<Agent | null>(null);
  const [confirmKillAgent, setConfirmKillAgent] = useState<Agent | null>(null);
  const [confirmArchiveAll, setConfirmArchiveAll] = useState<{
    bucket: AgentTimeBucket;
    label: string;
    agents: Agent[];
  } | null>(null);

  const archiveAllMutate = archiveAllAgents.mutate;

  const archiveAgentNow = useCallback(
    (agent: Agent) => {
      // Archiving is non-destructive: it only flips the archive flag, so the
      // agent keeps running and can be unarchived later. It never stops or
      // tears down the sandbox.
      //
      // The successor is read before the mutation so it is measured against the
      // list as rendered, not against whatever the optimistic flip leaves.
      const leaving = selectedIdRef.current === agent.id;
      const next = leaving ? successorTo(new Set([agent.id])) : undefined;
      archiveMutate(agent.id);
      if (leaving) onDeselect(next);
    },
    [archiveMutate, onDeselect, successorTo],
  );

  const handleArchive = useCallback(
    (agent: Agent) => {
      // Archiving a still-working agent is easy to do by accident, so confirm
      // first — but the archive itself stays non-destructive: the run keeps
      // going. The derived activity is what decides: a `running` agent that is
      // idle between turns, waiting for input, or interrupted has no turn in
      // flight, so it archives straight away.
      if (shouldConfirmArchive(agent, activitiesRef.current.get(agent.id))) {
        setConfirmArchiveAgent(agent);
        return;
      }
      archiveAgentNow(agent);
    },
    [archiveAgentNow],
  );

  function handleConfirmArchiveActive() {
    if (!confirmArchiveAgent) return;
    archiveAgentNow(confirmArchiveAgent);
    setConfirmArchiveAgent(null);
  }

  const handleArchiveAll = useCallback(
    (bucket: AgentTimeBucket, agents: Agent[], label: string) => {
      if (!agents.length) return;
      setConfirmArchiveAll({ bucket, label, agents });
    },
    [],
  );

  function handleConfirmArchiveAll() {
    if (!confirmArchiveAll) return;
    // Archive every matching run server-side rather than looping over the
    // agents the client has loaded: the bucket's calendar bounds (plus the
    // active owner filter) let the server sweep the whole set. Deselect if
    // the open agent falls inside the bucket being archived.
    const { bucket, agents: visible } = confirmArchiveAll;
    const bounds = getTimeBucketBounds(bucket);
    archiveAllMutate({
      ...(bounds.from !== undefined ? { createdAtFrom: bounds.from } : {}),
      ...(bounds.to !== undefined ? { createdAtTo: bounds.to } : {}),
      ...(filters.userId ? { userId: filters.userId } : {}),
    });
    deselectIfOpenIn(new Set(visible.map((a) => a.id)));
    setConfirmArchiveAll(null);
  }

  const handleUnarchive = useCallback(
    (agent: Agent) => {
      unarchiveMutate(agent.id);
    },
    [unarchiveMutate],
  );

  // Deleting a run is irreversible (it tears the sandbox down with it), so the
  // row menu asks first rather than deleting on the click; `DeleteAgentDialog`
  // owns the question, the mutation, and leaving the run's view when it was the
  // open one.
  const handleKill = useCallback((agent: Agent) => {
    setConfirmKillAgent(agent);
  }, []);

  const handleTogglePin = useCallback(
    (agent: Agent) => {
      if (agent.pinned) unpinMutate(agent.id);
      else pinMutate(agent.id);
    },
    [pinMutate, unpinMutate],
  );

  // `useSessionStorageState`'s setter is `useCallback`-wrapped over a
  // module-cached store entry (one per `session:<key>`, alive for the page's
  // lifetime), so it is referentially stable and listing it re-creates nothing.
  // React's lint rule only special-cases `useState`'s setter, which is why it
  // has to be declared here rather than assumed.
  const togglePinnedCollapsed = useCallback(() => {
    setPinnedCollapsed((prev) => !prev);
  }, [setPinnedCollapsed]);

  const toggleDraftsCollapsed = useCallback(() => {
    setDraftsCollapsed((prev) => !prev);
  }, [setDraftsCollapsed]);

  const handleDeleteDraft = useCallback(
    (draft: AgentDraft) => {
      deleteDraft.mutate(draft.id, {
        onSuccess: () => {
          if (selectedDraftId === draft.id) onDeselect();
        },
      });
    },
    [deleteDraft, onDeselect, selectedDraftId],
  );

  const sessionGroups = filters.draftsOnly
    ? groupedDraftsOnly
    : groupedByGroupView
      ? groupedByGroup
      : groupedSessions;
  const hasVisibleSessions = filters.draftsOnly
    ? filteredDrafts.length > 0
    : filters.archivedOnly
      ? archivedAgents.length > 0
      : sortedActive.length > 0 || filteredDrafts.length > 0;

  // --- Multi-select ---------------------------------------------------------
  // Sessions can be bulk archived/deleted. Two ways in, both adding no chrome
  // while idle: the header "Select" toggle, or shift-clicking any row (which
  // toggles just that row's selection — shift-click again to unselect). Select
  // mode is therefore derived: it is on whenever the header toggle is held on
  // or anything is selected, so shift-clicking the last row off drops you back
  // to normal automatically.
  const [headerSelect, setHeaderSelect] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [addLabelOpen, setAddLabelOpen] = useState(false);
  const selectMode = headerSelect || selectedIds.size > 0;

  // Active agents in visual order (Pinned, then the session groups). Drafts are
  // intentionally excluded — separate entity.
  const orderedActiveAgents = useMemo(() => {
    const list = [...pinnedActive];
    for (const group of sessionGroups) {
      for (const item of group.items) {
        if (item.kind === 'agent') list.push(item.agent);
      }
    }
    return list;
  }, [pinnedActive, sessionGroups]);

  // Selectable agents, which is the above plus Archived when it is expanded.
  const orderedSelectableIds = useMemo(() => {
    const ids = orderedActiveAgents.map((a) => a.id);
    if (showArchived) for (const a of archivedAgents) ids.push(a.id);
    return ids;
  }, [orderedActiveAgents, showArchived, archivedAgents]);
  const orderedIdsRef = useRef(orderedSelectableIds);
  useEffect(() => {
    orderedIdsRef.current = orderedSelectableIds;
    orderedActiveRef.current = orderedActiveAgents;
  });

  const agentById = useMemo(() => {
    const map = new Map<string, Agent>();
    for (const a of sortedActive) map.set(a.id, a);
    for (const a of archivedAgents) map.set(a.id, a);
    return map;
  }, [sortedActive, archivedAgents]);

  // Drop any selected ids that are no longer visible (e.g. filtered out) rather
  // than clearing the whole selection, so narrowing the list keeps the rest.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(orderedSelectableIds);
      const next = new Set<string>();
      for (const id of prev) if (valid.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [orderedSelectableIds]);

  const exitSelectMode = useCallback(() => {
    setHeaderSelect(false);
    setSelectedIds(new Set());
  }, []);

  // Header toggle: when select mode is already on (armed or anything selected)
  // it means "Done" and clears everything; otherwise it arms the mode with an
  // empty selection.
  const toggleSelectMode = () => {
    if (selectMode) exitSelectMode();
    else setHeaderSelect(true);
  };

  // Plain single toggle for one row (no range select): shift-click and an
  // in-mode plain click both route here to flip just this row.
  const toggleSelect = useCallback((agent: Agent) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(agent.id)) next.delete(agent.id);
      else next.add(agent.id);
      return next;
    });
  }, []);

  const allSelected =
    orderedSelectableIds.length > 0 && orderedSelectableIds.every((id) => selectedIds.has(id));
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      orderedIdsRef.current.every((id) => prev.has(id))
        ? new Set()
        : new Set(orderedIdsRef.current),
    );
  }, []);

  const selectedAgents = useMemo(
    () =>
      [...selectedIds].map((id) => agentById.get(id)).filter((a): a is Agent => a !== undefined),
    [selectedIds, agentById],
  );
  const hasArchivable = useMemo(() => selectedAgents.some((a) => !a.archived), [selectedAgents]);
  const selectedAgentIds = useMemo(() => selectedAgents.map((a) => a.id), [selectedAgents]);

  // Interruptible = mid-turn: `activityFromDurableAgentState` reports `working`
  // for a running agent with a turn in flight — or with only background tasks
  // left after its turn, which has nothing to interrupt. Interrupting an
  // idle/terminal agent is a no-op, so Stop is offered only when the selection
  // has one to act on.
  const interruptibleIds = useMemo(
    () =>
      selectedAgents
        .filter((a) => activities.get(a.id) === 'working' && !hasBackgroundOnlyWork(a))
        .map((a) => a.id),
    [selectedAgents, activities],
  );
  const hasInterruptible = interruptibleIds.length > 0;

  // Escape leaves select mode, unless the delete confirm is open (there Escape
  // should just dismiss the dialog).
  useEffect(() => {
    if (!selectMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmDelete) exitSelectMode();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectMode, confirmDelete, exitSelectMode]);

  const deselectIfOpenIn = useCallback(
    (ids: ReadonlySet<string>) => {
      if (selectedIdRef.current && ids.has(selectedIdRef.current)) onDeselect(successorTo(ids));
    },
    [onDeselect, successorTo],
  );

  const stopViewingDeleted = useStopViewingDeletedAgents();

  // Archiving is non-destructive (it does not tear down the devbox), so it
  // applies immediately with no confirmation.
  const handleBulkArchive = useCallback(() => {
    const ids = selectedAgents.filter((a) => !a.archived).map((a) => a.id);
    if (ids.length) {
      // Successor first, for the same reason as `archiveAgentNow`: it must be
      // measured against the list as rendered.
      deselectIfOpenIn(new Set(ids));
      bulkArchive.mutate(ids);
    }
    exitSelectMode();
  }, [selectedAgents, bulkArchive, deselectIfOpenIn, exitSelectMode]);

  // Interrupting only cancels the in-flight turn (the devbox and the agent row
  // survive), so — like archive — it applies immediately with no confirmation
  // and leaves the agents in place. Deliberately not equivalent to delete.
  const handleBulkStop = useCallback(() => {
    if (interruptibleIds.length) {
      bulkInterrupt.mutate(interruptibleIds);
    }
    exitSelectMode();
  }, [interruptibleIds, bulkInterrupt, exitSelectMode]);

  // Unlike the archive sweeps above, a bulk delete removes the runs outright,
  // so the open one can't be deselected in favour of its neighbour — it takes
  // the same route out as a single delete: back to the empty agents index.
  const handleConfirmBulkDelete = useCallback(() => {
    const ids = [...selectedIds];
    if (ids.length) {
      bulkKill.mutate(ids);
      stopViewingDeleted(ids);
    }
    setConfirmDelete(false);
    exitSelectMode();
  }, [selectedIds, bulkKill, stopViewingDeleted, exitSelectMode]);

  const canSelect = sortedActive.length > 0 || archivedAgents.length > 0;

  return (
    <div
      data-tutorial-id="agent-sidebar"
      className={cn(
        'flex h-full w-full flex-col overflow-hidden',
        mobile ? 'bg-background' : 'bg-panel',
      )}
    >
      {/* The banner slot survives without the header: tutorial page banners
          (TutorialPageBanner) portal into it above the filter bar. */}
      {variant === 'dock' && (
        <div
          data-page-banner-region=""
          className="shrink-0 border-b border-border/40 p-2 empty:hidden empty:border-b-0 empty:p-0"
        />
      )}
      {variant === 'panel' && (
        <SidebarHeader
          title="Agent Sessions"
          docs="concepts/agents"
          actions={
            <>
              <ToolbarIconButton
                icon={Plus}
                label="New agent"
                className="text-muted-foreground"
                onClick={onLaunchAgent}
              />
              <CollapseSidebarButton onClick={onToggle} />
            </>
          }
        />
      )}

      <div
        data-tutorial-id="agent-filter-bar"
        className={cn(
          'shrink-0',
          variant === 'dock'
            ? 'px-2 pb-1.5 pt-2'
            : [
                'flex items-start gap-1.5 border-b px-3 py-1.5',
                filtersActive ? 'border-ring/20 bg-ring/[0.03]' : 'border-border/40',
              ],
        )}
      >
        {/* Dock: one quiet pill, like the palette's Find bar — the shared
            search-field shell (see `searchFieldShellClasses` for the
            rest/hover/focus treatment), the input's own chrome fully
            stripped (including its dark-mode fill and focus ring, making the
            shell's focus halo the keyboard-focus affordance), and the
            list-settings menu aligned inside at the end. The filter drawer
            expands within the pill. */}
        <div className="flex items-start gap-1">
          <div
            data-testid="agent-search-pill"
            className={cn(
              'flex min-w-0 flex-1 items-start gap-1',
              variant === 'dock' && [
                searchFieldShellClasses,
                'py-1 pl-1 pr-1',
                '[&_input]:h-7 [&_input]:border-0 [&_input]:bg-transparent [&_input]:shadow-none [&_input]:focus-visible:ring-0 [&_input]:dark:bg-transparent',
              ],
            )}
          >
            <AgentFilterBar
              className="min-w-0 flex-1"
              filtersResult={agentFilters}
              agents={allAgents ?? []}
            />
            {variant === 'dock' && (
              <AgentSidebarViewMenu
                selectMode={selectMode}
                onToggleSelectMode={toggleSelectMode}
                canSelect={canSelect}
              />
            )}
          </div>
          {variant === 'panel' && (
            <AgentSidebarViewMenu
              selectMode={selectMode}
              onToggleSelectMode={toggleSelectMode}
              canSelect={canSelect}
            />
          )}
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-1 py-1">
          {!hasVisibleSessions &&
            (agentFilters.filters.query && !archivedAgents.length ? (
              <div
                data-testid="agent-sidebar-no-results"
                className="flex flex-col items-center gap-2 px-3 py-8 text-center"
              >
                <span className="text-xs text-muted-foreground/60">
                  No agents found for “{agentFilters.filters.query}”
                </span>
                <Button
                  variant="link"
                  onClick={() => agentFilters.setQuery(null)}
                  className="h-auto p-0 text-xs font-normal text-muted-foreground underline-offset-2 hover:text-foreground"
                >
                  Clear search
                </Button>
              </div>
            ) : filters.archivedOnly ? (
              // Ordered after the search branch, so a query that matched
              // nothing still says so and offers to clear itself rather than
              // claiming the org has nothing archived.
              <div
                data-testid="agent-sidebar-no-archived"
                className="flex flex-col items-center gap-2 px-3 py-8 text-center"
              >
                <span className="text-xs text-muted-foreground/60">No archived sessions</span>
                <Button
                  variant="link"
                  onClick={() => agentFilters.setArchivedOnly(false)}
                  className="h-auto p-0 text-xs font-normal text-muted-foreground underline-offset-2 hover:text-foreground"
                >
                  Show all sessions
                </Button>
              </div>
            ) : !showArchived ? (
              <Button
                variant="ghost"
                onClick={onLaunchAgent}
                data-testid="agent-sidebar-empty-launch"
                aria-label="Launch new agent"
                className="group h-auto w-full flex-col items-center gap-2 rounded-none px-3 py-8 text-center font-normal hover:bg-transparent dark:hover:bg-transparent"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/30 text-muted-foreground/40 transition-colors group-hover:border-border group-hover:text-muted-foreground group-focus-visible:border-ring group-focus-visible:text-foreground">
                  <Plus className="size-4" />
                </span>
                <span className="text-xs text-muted-foreground/60 transition-colors group-hover:text-muted-foreground">
                  No agents running
                </span>
              </Button>
            ) : null)}
          {!filters.draftsOnly && (
            <DraftsSection
              drafts={sortedDrafts}
              collapsed={draftsCollapsed}
              selectedDraftId={selectedDraftId}
              onToggle={toggleDraftsCollapsed}
              onSelect={(draft) => onSelectDraft?.(draft)}
              onDelete={handleDeleteDraft}
            />
          )}

          {!filters.draftsOnly && (
            <PinnedAgentSection
              agents={pinnedActive}
              collapsed={pinnedCollapsed}
              selectedId={selectedId}
              activities={activities}
              onToggle={togglePinnedCollapsed}
              onSelect={onSelect}
              onArchive={handleArchive}
              onDelete={handleKill}
              onTogglePin={handleTogglePin}
              selectionMode={selectMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
            />
          )}

          <motion.div {...staggerContainer}>
            {sessionGroups.map((group) => (
              <AgentSidebarSessionGroup
                key={group.key}
                groupKey={group.key}
                label={group.label}
                items={group.items}
                collapsed={collapsedGroups.has(group.key)}
                selectedAgentId={selectedId}
                selectedDraftId={selectedDraftId}
                activities={activities}
                onToggle={toggleGroup}
                onSelectAgent={onSelect}
                onSelectDraft={(draft) => onSelectDraft?.(draft)}
                onArchiveAgent={handleArchive}
                onDeleteAgent={handleKill}
                onTogglePin={handleTogglePin}
                onDeleteDraft={handleDeleteDraft}
                onArchiveAll={groupedByGroupView ? undefined : handleArchiveAll}
                sectionContext={'group' in group ? group.group : null}
                onLaunchInGroup={onLaunchInGroup}
                selectionMode={selectMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
              />
            ))}
          </motion.div>

          {!filters.draftsOnly && (
            <ArchivedAgentSection
              agents={archivedAgents}
              // Under the Archived facet these rows are the whole list, so the
              // section drops its header rather than hiding them behind one
              // more disclosure — the thing this filter exists to avoid.
              collapsible={!filters.archivedOnly}
              show={showArchived}
              selectedId={selectedId}
              onToggleShow={() => setShowArchived(!showArchived)}
              onSelect={onSelect}
              onUnarchive={handleUnarchive}
              onDelete={handleKill}
              selectionMode={selectMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
            />
          )}
        </div>

        {/* The sentinel doubles as the loading footer, at a fixed height
            whether or not a fetch is in flight. Mounting the spinner only
            while fetching grew and shrank the scrollable area by its height on
            every page, and a page that adds no visible rows (a run of archived
            ones, say) shrank it with nothing to take up the slack — the
            browser clamped the scroll position back, the sentinel came into
            view again, and the list twitched through page after page. */}
        <div
          ref={sentinelRef}
          data-testid="agent-sidebar-load-more"
          className="flex h-8 items-center justify-center"
        >
          {isFetchingNextPage && <Spinner className="h-4 w-4 text-muted-foreground" />}
        </div>
      </ScrollArea>

      <AnimatePresence>
        {selectMode && (
          <AgentSelectionBar
            count={selectedIds.size}
            total={orderedSelectableIds.length}
            allSelected={allSelected}
            hasArchivable={hasArchivable}
            hasInterruptible={hasInterruptible}
            onToggleAll={toggleSelectAll}
            onArchive={handleBulkArchive}
            onStop={handleBulkStop}
            onAddToGroup={() => setAddToGroupOpen(true)}
            onAddLabel={() => setAddLabelOpen(true)}
            onDelete={() => setConfirmDelete(true)}
            onClose={exitSelectMode}
          />
        )}
      </AnimatePresence>

      {/* Filing the selection into a group. Kept mounted alongside the other
          selection dialogs so the picker's own state resets per opening. */}
      <AddToGroupDialog
        open={addToGroupOpen}
        onOpenChange={setAddToGroupOpen}
        agents={selectedAgents}
        onAdded={exitSelectMode}
      />

      <AddLabelDialog
        open={addLabelOpen}
        onOpenChange={setAddLabelOpen}
        agentIds={selectedAgentIds}
        onAdded={exitSelectMode}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete ${selectedIds.size} agent${selectedIds.size === 1 ? '' : 's'}?`}
        description="This permanently deletes the selected agents and tears down any running sandboxes. This cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleConfirmBulkDelete}
      />

      {confirmKillAgent && (
        <DeleteAgentDialog
          agent={confirmKillAgent}
          open
          onOpenChange={(open) => {
            if (!open) setConfirmKillAgent(null);
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmArchiveAgent}
        onOpenChange={(open) => {
          if (!open) setConfirmArchiveAgent(null);
        }}
        title="Archive this running agent?"
        description="This agent is still working. Archiving won't stop it; the run keeps going and you can unarchive it anytime."
        confirmLabel="Archive"
        onConfirm={handleConfirmArchiveActive}
      />

      <ConfirmDialog
        open={!!confirmArchiveAll}
        onOpenChange={(open) => {
          if (!open) setConfirmArchiveAll(null);
        }}
        title={
          confirmArchiveAll
            ? `Archive all agents in ${confirmArchiveAll.label}?`
            : 'Archive all agents?'
        }
        description="This archives every run in this group on the server, including any not loaded here. Running agents keep working and can be unarchived later."
        confirmLabel="Archive All"
        onConfirm={handleConfirmArchiveAll}
      />
    </div>
  );
});
